import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetGiftQuery,
  useUpdateGiftMutation,
} from '../../../redux/api/authorized/gifts';
import GiftCardForm from '../../../components/GiftCardForm';
import { useSolana, toTokenAmount } from '../../../hooks/useSolana';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
  selectTokenBySymbol,
} from '../../../redux/slices/tokenSlice';
import { selectTokenBalance } from '../../../redux/slices/walletSlice';
import { useSelector } from 'react-redux';
import CreateGiftSkeleton from '../CreateGift/CreateGiftSkeleton';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../redux/slices/modals';
import {
  EVENT_DEPOSIT_MODAL_OPENED,
  sendEvent,
  TOKEN,
} from '../../../common/analytics';

const EditGift = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fromTokenAmount } = useSolana();
  const dispatch = useDispatch();

  const { data, isLoading, error } = useGetGiftQuery(id);
  const [updateGift] = useUpdateGiftMutation();

  const gift = data?.gift;

  const token = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, gift?.network, gift?.token),
  );
  const baseToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, gift?.network, gift?.baseToken),
  );
  const usdcToken = useSelector((state) =>
    selectTokenBySymbol(state, gift?.network, 'USDC'),
  );
  const tokenObj = useSelector((state) =>
    selectTokenBySymbol(state, gift?.network, token?.symbol),
  );
  const usdcBalance = useSelector((state) =>
    selectTokenBalance(gift?.network, usdcToken?.address || '')(state),
  );
  const tokenBalance = useSelector((state) =>
    selectTokenBalance(gift?.network, tokenObj?.address || '')(state),
  );

  // Calculate the original input amount by adding the fee back
  const baseAmount = gift
    ? fromTokenAmount(gift.baseTokenAmount, baseToken?.decimals)
    : 0;
  const fee = gift?.fee ? fromTokenAmount(gift.fee, baseToken?.decimals) : 0;
  const originalInputAmount = parseFloat(baseAmount) + parseFloat(fee);

  const initialValues = gift
    ? {
        email: gift.receiver.email,
        message: gift.message || '',
        cardImage: gift.imageUrl,
        token: token?.address,
        network: token?.network,
        baseToken: baseToken?.address,
        baseTokenAmount: originalInputAmount.toString(),
      }
    : {};

  const handleSubmit = async (values) => {
    const baseTokenAmount = parseFloat(values.baseTokenAmount);
    const isUsingUSDC = values.baseToken === usdcToken.address;
    const payingToken = isUsingUSDC ? usdcToken : token;
    const payingTokenBalance = isUsingUSDC ? usdcBalance : tokenBalance;

    if (payingTokenBalance < baseTokenAmount) {
      sendEvent(EVENT_DEPOSIT_MODAL_OPENED, {
        from: TOKEN,
      });
      dispatch(
        openModal({
          name: 'addFunds',
          props: { amount: baseTokenAmount - payingTokenBalance },
        }),
      );
      return;
    }
    const updatedGift = await updateGift({
      giftId: id,
      ...values,
      baseTokenAmount: toTokenAmount(
        values.baseTokenAmount,
        payingToken?.decimals,
      ),
    }).unwrap();

    // Navigate to confirmation with the updated gift data
    navigate(`/app/gifts/${id}/confirm`, {
      state: { gift: updatedGift.gift },
    });
  };

  if (isLoading) {
    return <CreateGiftSkeleton />;
  }

  if (error) {
    return <div>Error loading gift</div>;
  }

  if (!gift || !token || !baseToken) {
    return <div>Gift not found</div>;
  }

  return (
    <GiftCardForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      submitButtonText='Update Gift'
      secondaryButtonText='Discard Changes'
      onSecondaryButtonClick={() => {
        navigate(`/app/gifts/${id}/confirm`);
      }}
      backButtonPath={`/app/tokens/${token?.address}`}
    />
  );
};

export default EditGift;
